import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { countryMasterAPI } from "../services/api";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import "./RaiseComplaint.css";
import "./AddEditCountryMaster.css";
import { getRoutePermissions } from "../utils/auth";

const normalizeCountry = (item) => ({
  countryCode: item.countryCode || item.CountryCode || item.COUNTRYCODE || "",
  countryId: item.countryId || item.CountryId || item.COUNTRYID || "",
  countryName: item.countryName || item.CountryName || item.COUNTRYNAME || "",
  isActive: item.isActive ?? item.IsActive ?? true,
});

const AddEditCountryMaster = () => {
  const navigate = useNavigate();
  const { countryCode } = useParams();
  const isEditMode = !!countryCode;

  const routePermissions = getRoutePermissions("/countries");
  const { canAdd, canEdit } = routePermissions;
  const canSave = isEditMode ? canEdit : canAdd;

  const [country, setCountry] = useState({
    countryCode: "",
    countryId: "",
    countryName: "",
    isActive: true,
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const currentUser = (() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  })();

  useEffect(() => {
    if (!canSave) {
      Swal.fire({
        title: "Access Denied",
        text: `You do not have permission to ${isEditMode ? "edit" : "add"} countries.`,
        icon: "error",
        confirmButtonText: "OK",
      }).then(() => navigate("/countries"));
      return;
    }
    if (isEditMode) {
      loadCountry();
    } else {
      loadNextCountryCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode, canSave]);

  const loadNextCountryCode = async () => {
    try {
      const response = await countryMasterAPI.getNextCode();
      if (response && response.success && response.data) {
        setCountry((prev) => ({ ...prev, countryCode: response.data }));
      }
    } catch (e) {
      console.error("Error loading next country code:", e);
      setCountry((prev) => ({ ...prev, countryCode: "C0000000001" }));
    }
  };

  const loadCountry = async () => {
    try {
      setLoading(true);
      const response = await countryMasterAPI.getByCode(countryCode);
      if (response && response.success && response.data) {
        setCountry(normalizeCountry(response.data));
      } else {
        navigate("/countries");
      }
    } catch (e) {
      console.error("Error loading country:", e);
      if (e.response?.status !== 404) {
        Swal.fire("Error", "Failed to load country details", "error");
      }
      navigate("/countries");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setCountry((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!country.countryCode?.trim()) {
      Swal.fire("Error", "Country Code is required", "error");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const createdBy = currentUser?.userIdCode || "SYSADMIN001";
      let response;

      if (isEditMode) {
        response = await countryMasterAPI.update(
          countryCode,
          country,
          createdBy
        );
      } else {
        response = await countryMasterAPI.create(country, createdBy);
      }

      if (response && response.success) {
        Swal.fire({
          title: "Success",
          text: response.message || "Country saved successfully",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          navigate("/countries");
        });
      } else {
        Swal.fire(
          "Error",
          response.message || "Failed to save country",
          "error"
        );
      }
    } catch (e) {
      console.error("Error saving country:", e);
      Swal.fire(
        "Error",
        e.response?.data?.message ||
          "Failed to save country. Please try again.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box className="add-edit-product-loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box className="add-edit-product-header">
        <Breadcrumbs
          aria-label="breadcrumb"
          className="add-edit-product-breadcrumb"
        >
          <Link
            underline="hover"
            color="inherit"
            href="/countries"
            onClick={(e) => {
              e.preventDefault();
              navigate("/countries");
            }}
            className="breadcrumb-link brdcrmbLstitms"
          >
            Country Master
          </Link>
          <Typography
            color="text.primary"
            className="breadcrumb-current brdcrmbLstitms brdcrmbCurPg"
          >
            {isEditMode ? "Edit Country" : "Add Country"}
          </Typography>
        </Breadcrumbs>
        <Box className="add-edit-product-header-right">
          <Button
            className="add-edit-product-back-button"
            onClick={() => navigate("/countries")}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          className="add-edit-product-error-alert"
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <div className="container-fluid add-edit-product-page">
        {/* Basic Information Section */}
        <div className="card-panel mb-3">
          <div className="row g-3 mb-4">
            <div className="col-12 col-md-6 col-lg-4" style={{ display: "none" }}>
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Country Code <span style={{ color: "red" }}>*</span>
                </label>
                <TextField
                  value={country.countryCode}
                  required
                  fullWidth
                  disabled={true}
                  size="small"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Country ID
                </label>
                <TextField
                  value={country.countryId}
                  onChange={(e) => handleChange("countryId", e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Enter country ID"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Country Name
                </label>
                <TextField
                  value={country.countryName}
                  onChange={(e) => handleChange("countryName", e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Enter country name"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="row mt-4">
          <div className="col-12">
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={() => navigate("/countries")}
                className="btn-cancel-product funbtns"
                startIcon={<CancelIcon />}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || !canSave}
                startIcon={
                  saving ? <CircularProgress size={20} /> : <SaveIcon />
                }
                className="btn-save-product funbtns"
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </Stack>
          </div>
        </div>
      </div>
    </Box>
  );
};

export default AddEditCountryMaster;



